"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useMotionTemplate, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef, type PointerEvent } from "react";
import type { Lang } from "@/lib/lang";
import { COPY } from "./copy";
import { ArrowIcon, CheckIcon, CountUp, Overline, Reveal, ShareIcon, useFillOnArrival, usePrefersReducedMotion } from "./ui";

// The example player's last eight overall ratings: the sparkline under the card.
const TREND = [70, 71, 71, 73, 74, 74, 76, 78];

function Sparkline() {
  const w = 300, h = 54, pad = 5;
  const min = Math.min(...TREND) - 1, max = Math.max(...TREND) + 1;
  const pts = TREND.map((v, i) => [pad + (i * (w - pad * 2)) / (TREND.length - 1), h - pad - ((v - min) / (max - min)) * (h - pad * 2)] as const);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-[54px] w-full" preserveAspectRatio="none" role="img" aria-label="70 → 78">
      <defs>
        <linearGradient id="sn-pass-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#E2BD72" stopOpacity=".32" />
          <stop offset="1" stopColor="#E2BD72" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L${w - pad},${h} L${pad},${h} Z`} fill="url(#sn-pass-fill)" />
      <path d={line} fill="none" stroke="#E2BD72" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={lx} cy={ly} r="4.5" fill="#E2BD72" stroke="#05070d" strokeWidth="2.5" />
    </svg>
  );
}

function PassportCard({ lang }: { lang: Lang }) {
  const k = COPY.passport;
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const barsRef = useRef<HTMLDivElement>(null);
  useFillOnArrival(barsRef);

  // Pointer tilt with a sheen that follows the light.
  const px = useMotionValue(0.5), py = useMotionValue(0.5);
  const rx = useSpring(useTransform(py, [0, 1], [7, -7]), { stiffness: 160, damping: 18 });
  const ry = useSpring(useTransform(px, [0, 1], [-9, 9]), { stiffness: 160, damping: 18 });
  const gx = useTransform(px, [0, 1], ["0%", "100%"]);
  const gy = useTransform(py, [0, 1], ["0%", "100%"]);
  const sheen = useMotionTemplate`radial-gradient(520px circle at ${gx} ${gy}, rgba(255,255,255,.16), rgba(255,255,255,0) 45%)`;
  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    if (reduced || e.pointerType === "touch") return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { px.set(0.5); py.set(0.5); };

  return (
    <div className="relative mx-auto w-full max-w-[440px]" style={{ perspective: 1400 }}>
      {/* Gold floodlight behind the card. */}
      <div className="pointer-events-none absolute -inset-16 -z-10 opacity-70" style={{ background: "radial-gradient(closest-side, rgba(226,189,114,.22), rgba(226,189,114,0))" }} />
      {/* A second Passport in the stack: every player gets one. */}
      <div
        aria-hidden="true"
        className="absolute -left-[34%] top-20 hidden w-[68%] -rotate-[10deg] overflow-hidden rounded-[28px] xl:block"
        style={{ background: "var(--sn-surface)", boxShadow: "0 40px 90px -30px rgba(0,0,0,.9), 0 0 0 1px rgba(255,255,255,.1)", opacity: 0.72 }}
      >
        <div className="relative aspect-[4/5]">
          <Image src="/landing/player-2.webp" alt="" fill sizes="330px" className="object-cover" style={{ objectPosition: "50% 20%" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,13,0) 45%, #0e1119 96%)" }} />
          <div className="absolute inset-x-5 bottom-4 flex items-end gap-3">
            <span className="sn-num text-[56px] leading-[0.8]" style={{ color: "var(--sn-gold)" }}>{k.second.rating}</span>
            <span className="sn-wide pb-0.5 text-[14px]">{k.second.position[lang]}</span>
          </div>
        </div>
      </div>
      <motion.div
        ref={ref}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
        className="relative z-10 overflow-hidden rounded-[32px]"
        style={{
          rotateX: reduced ? 0 : rx, rotateY: reduced ? 0 : ry, transformStyle: "preserve-3d",
          background: "var(--sn-surface)",
          boxShadow: "0 50px 120px -40px rgba(0,0,0,.95), 0 0 0 1px rgba(255,255,255,.1)",
        }}
      >
        <div className="relative aspect-[4/4.4]">
          <Image src="/landing/player.webp" alt={k.alt[lang]} fill sizes="440px" className="object-cover" style={{ objectPosition: "50% 22%" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,13,.45) 0%, rgba(5,7,13,0) 22%, rgba(5,7,13,.2) 52%, #0e1119 97%)" }} />
          <div className="absolute inset-x-5 top-5 flex items-center justify-between">
            <span className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.38em]" style={{ color: "var(--sn-gold)" }}>
              <Image src="/landing/d-mark.webp" alt="" width={523} height={378} className="h-[17px] w-auto" />
              PASSPORT
            </span>
            <span className="rounded-full px-2.5 py-1 text-[10.5px] font-semibold" style={{ background: "rgba(5,7,13,.55)", color: "var(--sn-fg2)", backdropFilter: "blur(8px)" }}>
              {k.example[lang]}
            </span>
          </div>
          <div className="absolute inset-x-6 bottom-4">
            <div className="flex items-end gap-4">
              <CountUp value={78} className="sn-num text-[96px] leading-[0.78]" style={{ color: "var(--sn-gold)" }} />
              <div className="flex flex-col gap-1 pb-1">
                <span className="sn-wide text-[17px]">{k.position[lang]}</span>
                <span className="text-[12px] font-medium" style={{ color: "var(--sn-fg2)" }}>{k.overall[lang]}</span>
              </div>
            </div>
            <p className="sn-wide mt-4 text-[30px] leading-[1.02]">{k.name[lang]}</p>
            <p className="mt-1.5 text-[13px] font-medium" style={{ color: "var(--sn-fg2)" }}>{k.team[lang]}</p>
          </div>
        </div>
        <div className="px-6 pb-6 pt-3">
          <div ref={barsRef} className="grid grid-cols-2 gap-x-6 gap-y-3.5">
            {k.attrs.map((a) => (
              <div key={a.label.en} className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[12.5px] font-medium" style={{ color: "var(--sn-fg2)" }}>{a.label[lang]}</span>
                  <span className="sn-num text-[17px] leading-none">{a.value}</span>
                </div>
                <div className="h-[3px] overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,.1)" }}>
                  <div data-fill className="h-full rounded-full" style={{ width: `${a.value}%`, background: "var(--sn-gold)", transformOrigin: "left" }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-baseline justify-between">
            <span className="sn-over !text-[10px]">{k.trend[lang]}</span>
            <span className="text-[12px] font-semibold" style={{ color: "var(--sn-good)" }}>{k.trendValue[lang]}</span>
          </div>
          <Sparkline />
          <div className="mt-4 flex h-11 items-center justify-center gap-2 rounded-full text-[13.5px] font-semibold" style={{ background: "var(--sn-glass-strong)" }}>
            <ShareIcon size={15} /> {k.share[lang]}
          </div>
        </div>
        <motion.div className="pointer-events-none absolute inset-0" style={{ background: sheen }} />
      </motion.div>
    </div>
  );
}

export function Passport({ lang }: { lang: Lang }) {
  const k = COPY.passport;
  return (
    <section id="passport" className="relative scroll-mt-16 overflow-hidden py-24 sm:py-32 lg:py-40">
      <div className="mx-auto grid max-w-[1360px] items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1fr] lg:gap-24">
        <Reveal y={40}><PassportCard lang={lang} /></Reveal>
        <div>
          <Reveal><Overline gold>{k.over[lang]}</Overline></Reveal>
          <Reveal delay={0.06}><h2 className="sn-wide mt-4 text-[clamp(32px,4.6vw,66px)]">{k.title[lang]}</h2></Reveal>
          <Reveal delay={0.12}><p className="sn-body mt-6">{k.body[lang]}</p></Reveal>
          <ul className="mt-8 flex flex-col gap-3.5">
            {k.points.map((p, i) => (
              <Reveal key={p.en} delay={0.16 + i * 0.05}>
                <li className="flex items-center gap-3 text-[15.5px] font-medium">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ background: "rgba(62,207,142,.14)", color: "var(--sn-good)" }}>
                    <CheckIcon size={14} />
                  </span>
                  {p[lang]}
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.3}>
            <Link href="/get-app" className="sn-gold-btn mt-10">{k.cta[lang]} <ArrowIcon /></Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
